import { markRaw } from 'vue'

import { Entity } from '../utils/common'
import { EntityEvent } from '../utils/enums'
import { EntityErrors } from '../utils/types'
import { CashFlowProjection } from './utils/types'

import { CASHFLOW_PROJECTION_DEFAULT_DATA, CASHFLOW_PROJECTION_FIELD } from './utils/const'
import { ASSET_FIELD } from '@/const'

import { Rule, rules } from '@/helpers/validate'
import { parseDate } from '@/helpers/dates'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useCashFlowProjectionsBunchStore } from '@/store/cashflow-projections/bunch'
import { useCashFlowProjectionsStore } from '@/store/cashflow-projections'

import CashflowProjectionDialog from './components/CashflowProjectionDialog.vue'
import CashflowProjectionDrawer from './components/CashflowProjectionDrawer.vue'
import CashflowProjectionFormDate from './components/CashflowProjectionFormDate.vue'
import CashflowProjectionFormDescription from './components/CashflowProjectionFormDescription.vue'
import CashflowProjectionFormPosition from './components/CashflowProjectionFormPosition.vue'
import CashflowProjectionFormValue from './components/CashflowProjectionFormValue.vue'

export class CashFlowProjectionClass extends Entity<CashFlowProjection> {
  private bunchStore
  private cashFlowProjectionsStore
  private assetsBunchStore

  constructor(data: Partial<CashFlowProjection>, created = false) {
    super(data, CASHFLOW_PROJECTION_DEFAULT_DATA, created)
    this.bunchStore = useCashFlowProjectionsBunchStore()
    this.cashFlowProjectionsStore = useCashFlowProjectionsStore()
    this.assetsBunchStore = useAssetsBunchStore()
  }

  getFormPosition() {
    return CashflowProjectionFormPosition
  }

  getFormDate() {
    return CashflowProjectionFormDate
  }

  getFormValue() {
    return CashflowProjectionFormValue
  }

  getFormDescription() {
    return CashflowProjectionFormDescription
  }

  validate(): Record<string, Rule> {
    return {
      account_id: rules.required,
      asset_id: rules.required,
      date: rules.date,
      value: rules.number,
    }
  }

  get displayPosition() {
    const accountId = this.field<string>(CASHFLOW_PROJECTION_FIELD.ACCOUNT_ID).value
    const assetId = this.field<string>(CASHFLOW_PROJECTION_FIELD.ASSET_ID).value
    if (!accountId || !assetId) return
    const accountName = this.assetsBunchStore.getElementById(accountId)?.field<string>(ASSET_FIELD.NAME).value
    const assetName = this.assetsBunchStore.getElementById(assetId)?.field<string>(ASSET_FIELD.NAME).value
    return `${accountName} - ${assetName}`
  }

  pasteDate(value: string) {
    return new Promise((resolve, reject) => {
      const { isoDate, isAmbiguous } = parseDate(value)
      if (isAmbiguous) {
        reject(`The pasted date "${value}" is ambiguous and could not be processed.`)
        return
      }
      if (!isoDate) {
        reject('Invalid date')
        return
      }
      this.field(CASHFLOW_PROJECTION_FIELD.DATE).value = isoDate
      resolve(true)
    })
  }

  pasteValue(value: string) {
    return new Promise((resolve, reject) => {
      if (value.trim() === '') {
        this.field(CASHFLOW_PROJECTION_FIELD.VALUE).value = null
        resolve(true)
        return
      }

      let amount = Number(value.replaceAll(',', ''))
      if (isNaN(amount)) {
        reject('Invalid amount')
        return
      }

      this.field(CASHFLOW_PROJECTION_FIELD.VALUE).value = amount
      super.paste()
      resolve(true)
    })
  }

  handleErrors(errors: EntityErrors): EntityErrors {
    return errors
  }

  get getModal() {
    return markRaw(this.isDialog ? CashflowProjectionDialog : CashflowProjectionDrawer)
  }

  private removeFromBunch() {
    this.bunchStore.getList.delete(this.id)
  }

  remove() {
    this.removeFromBunch()
  }

  async store(): Promise<void> {
    try {
      const data = this.get()
      const result = await this.cashFlowProjectionsStore.store(data)
      this.remove()
      if (result !== undefined) {
        this.reset(result)
        this.bunchStore.unshiftElement(this)
        this.callEvent(EntityEvent.STORED)
      }
    } catch (e) {
      throw Error(e as string)
    }
  }

  async update(): Promise<void> {
    try {
      const result = await this.cashFlowProjectionsStore.update(this.get())
      if (result === undefined) {
        this.cancel()
      } else {
        this.callEvent(EntityEvent.UPDATED)
        this.reset(result)
      }
    } catch (e) {
      throw Error(e as string)
    }
  }

  async destroy(): Promise<void> {
    try {
      if (!this.isNew) {
        await this.cashFlowProjectionsStore.destroy(this.id)
        this.callEvent(EntityEvent.DESTROYED)
      }
      this.remove()
    } catch (e) {
      throw Error(e as string)
    }
  }
}
